import React from 'react'
import _ from 'lodash'
import wordWrap from 'word-wrap'
import * as styles from './styles'
import {
  isRef,
  resolveOpenApiRef,
  refNameToSlug,
  refNameToPretty,
  firstSentence,
  ensureTrailingDot
} from '../../util'

const BRACKET_START = String.fromCharCode(91)
const BRACKET_END = String.fromCharCode(93)
const CURLY_BRACE_START = String.fromCharCode(123)
const CURLY_BRACE_END = String.fromCharCode(125)

const INDENT_SPACES_SIZE = 2

function createIndent(indentLevel, spaces = INDENT_SPACES_SIZE) {
  return _.repeat(' ', (indentLevel * spaces))
}

function getDescription(openapi, obj, opts = {}) {
  const resolvedRef = resolveOpenApiRef(openapi.bundle, obj)
  let rawDescription = obj.description || resolvedRef.description || undefined
  if (!_.isString(rawDescription)) {
    return null
  }

  let description = ensureTrailingDot(firstSentence(rawDescription))
  if (opts.isRequired) {
    description += ' Required.'
  } else if (!_.isUndefined(obj.default)) {
    description += `\nDefault: ${obj.default}`
  }

  return description
}

function createComment(text, _opts = {}) {
  const opts = _.merge({
    prefix: '// ',
    maxLineLength: 60,
    indentLevel: 0,
    indentSpaces: INDENT_SPACES_SIZE,
  }, _opts)

  return wordWrap(text, {
    indent: `${createIndent(opts.indentLevel, opts.indentSpaces)}${opts.prefix}`,
    width: opts.maxLineLength,
  })
}

function createObject({ openapi, content, schema, indent, keyPrefix }) {
  const items = [<styles.Text key={`${keyPrefix}.curlyBraceStart`}>{CURLY_BRACE_START}</styles.Text>]

  const isUserDefinedObject =
	schema.__refName === 'UserDefinedObject'
  if (isUserDefinedObject) {
    return <NativeType type="object" />
  }
  // XXX: fix me later
  const isEmptyObject =
	schema.__refName === 'OptimizeOperation' ||
	schema.__refName === 'OptimizeOperationResponse' ||
	schema.__refName === 'EmptyDocument'
    
  if (!isEmptyObject && _.keys(schema.properties) < 1) {
    const str = process.env.NODE_ENV === 'production' ? 'object' : 'object (TODO)'
    return <NativeType type={str} />
  }

  const propertiesArr = _.map(schema.properties, (val, key) => ({ val, key }))
  _.forEach(propertiesArr, (item, index) => {
    const { val, key } = item

    const addCommaToEnd = index < propertiesArr.length - 1
    const isRequired = _.includes(schema.required, key)
    const commentContent = getDescription(openapi, val, { isRequired })
    items.push(
      <div key={`${keyPrefix}.${key}`}>
        <styles.Block>
          <styles.Comment>
            {createComment(commentContent, { indentLevel: indent + 1 })}
          </styles.Comment>
        </styles.Block>
        <styles.Block>
          {createIndent(indent + 1)}
          <styles.Key>{`"${key}": `}</styles.Key>
          <styles.Text>
            {
              create({
                openapi,
                content,
                schema: val,
                indent: indent + 1,
                keyPrefix: `${keyPrefix}.${key}`
              })
            }
            { addCommaToEnd ? ',' : '' }
          </styles.Text>
        </styles.Block>
      </div>
    )
  })

  const lastIndentLevel = propertiesArr.length > 0
    ? indent
    // We want to inline all empty objects
    : 0

  items.push(
    <styles.Text key={`${keyPrefix}.curlyBraceEnd`}>
      {createIndent(lastIndentLevel)}
      {CURLY_BRACE_END}
    </styles.Text>
  )

  return items
}


function createArray({ openapi, content, schema, indent, keyPrefix }) {
  const items = [<styles.Text key={`${keyPrefix}.bracketStart`}>{BRACKET_START}</styles.Text>]

  const minItems = _.isFinite(schema.minItems) ? schema.minItems : 1
  const hasInfiniteItems = !_.isFinite(schema.maxItems)
  for (let i = 0; i < minItems; ++i) {
    const isLastItem = i === minItems - 1
    items.push(
      <styles.Text key={`${keyPrefix}.${i}`}>
        {
          create({
            openapi,
            content,
            schema: schema.items,
            indent: indent + 1,
            keyPrefix: `${keyPrefix}.${i}`
          })
        }
        { (!isLastItem || hasInfiniteItems) ? ', ' : '' }
      </styles.Text>
    )
  }

  if (hasInfiniteItems) {
    items.push(<styles.Text key={`${keyPrefix}.ellipsis`}>{'...'}</styles.Text>)
  }

  items.push(<styles.Text key={`${keyPrefix}.bracketEnd`}>{BRACKET_END}</styles.Text>)
  return items
}

function createRefLink({ openapi, content, refObj }) {
  const resolvedRef = resolveOpenApiRef(openapi.bundle, refObj)
  const slug = refNameToSlug(resolvedRef.__refName)
  return (
    <a href={`#${slug}`}>{refNameToPretty(content, resolvedRef.__refName)}</a>
  )
}

function createOneOf({ openapi, content, schema, indent, keyPrefix }) {
  const newIndent = indent > 0
    ? indent + 1
    : 0

  const uniqOneOfs = _.uniqBy(schema.oneOf, (one) => {
    if (isRef(one)) {
      return `ref-${one.$ref}`
    }

    return one.type
  })

  return (
    <React.Fragment>
      {
        _.map(uniqOneOfs, (itemSchema, i) => {
          const isLast = i === uniqOneOfs.length - 1
          return (
            <div key={`${keyPrefix}.oneOf.${i}`}>
              <styles.OneOfBlock>
                <styles.Text>
                  {createIndent(newIndent)}
                  { create({ openapi, content, schema: itemSchema, indent: newIndent, keyPrefix }) }
                </styles.Text>
                { isLast ? null : <styles.OneOfOr>{createIndent(newIndent)}or</styles.OneOfOr> }
              </styles.OneOfBlock>
            </div>
          )
        })
      }
    </React.Fragment>
  )
}

function createAllOf({ openapi, content, schema, indent, keyPrefix }) {
  const newSchema = _.reduce(schema.allOf, (memo, itemSchema) => {
    const resolvedItemSchema = resolveOpenApiRef(openapi.bundle, itemSchema)
    if (resolvedItemSchema.type !== 'object') {
      throw new Error(`All schemas under anyOf must be objects, found: ${resolvedItemSchema.type}`)
    }

    return _.merge({}, memo, resolvedItemSchema)
  }, {})
  return createObject({ openapi, content, schema: newSchema, indent, keyPrefix })
}

const NativeType = (props) => {
  return <styles.NativeType>{props.type}</styles.NativeType>
}

function create({ openapi, content, schema, indent = 0, keyPrefix = '' }) {
  if (_.has(schema, 'oneOf')) {
    return createOneOf({ openapi, content, schema, indent, keyPrefix })
  }

  if (_.has(schema, 'allOf')) {
    return createAllOf({ openapi, content, schema, indent, keyPrefix })
  }

  if (isRef(schema)) {
    return createRefLink({ openapi, content, refObj: schema, indent, keyPrefix })
  }

  switch (schema.type) {
    case 'object':
      return createObject({ openapi, content, schema, indent, keyPrefix })

    case 'array':
      return createArray({ openapi, content, schema, indent, keyPrefix })

    case 'number':
      return <NativeType type="number" />
    case 'integer':
      return <NativeType type="integer" />
    case 'string':
      return <NativeType type="string" />
    case 'boolean':
      return <NativeType type="boolean" />
    case 'null':
        return <NativeType type="null" />

    default:
      console.error(schema)
      throw new Error(`Schema type not supported: ${schema.type}`)
  }
}

export default ({ openapi, content, schema, indent = 0 }) => {
  return (
    <styles.Container>
      {create({ openapi, content, schema, indent })}
    </styles.Container>
  )
}
